<template>
    <div>
        <div class="header-with-tiles">
            <div class="email-page-header">
                <div class="title-arrow">
                    <ds-text-button gray as="a" href="/Dashboard/userDashboard.jsp">
                        <ds-icon name="arrow-left" />
                    </ds-text-button>

                    <div>Bounces</div>
                </div>

                <a class="feedback-link" @click="isFeedbackModalOpen=true">
                    <ds-icon name="message" />
                    <div>
                        Leave feedback
                    </div>
                </a>

                <share-feedback-modal
                    :is-open="isFeedbackModalOpen"
                    :feature="$options.EMAIL_ANALYTICS"
                    :slack-channels="[$options.SLACK_CHANNELS.IN_APP_FEEDBACK, $options.SLACK_CHANNELS.EMAIL_ANALYTICS_FEEDBACK]"
                    @close="isFeedbackModalOpen = false"
                />
            </div>

            <ds-inline-alert
                v-if="showMessage"
                leading-icon
                type="critical"
                class="alert-banner"
            >
                {{ getAlertBannerMessage }}
                <ds-inline-alert-footer-link
                    v-if="showAlertBannerLink"
                    href="https://help.infusionsoft.com/help/contact-support"
                >
                    Contact Support
                </ds-inline-alert-footer-link>
            </ds-inline-alert>

            <div class="bounce-details-page">
                <ds-card class="card">
                    <ds-card-header class="card-title">
                        <div v-if="isEmailHealthContentUpdateEnabled" class="email-card-header">
                            Invalid bounce rate
                        </div>

                        <div v-else class="email-card-header">
                            Bounce rate
                        </div>

                        <div />

                        <div class="last-30-days">
                            Last 30 days
                        </div>
                    </ds-card-header>

                    <ds-card-body>
                        <progress-bar-pi-chart-card
                            :data-by-type="emailBouncesByType"
                            :progress-bar-value="bounceRate"
                            :progress-bar-upper-limit="bounceRateUpperLimit"
                            :progress-bar-healthy-limit="bounceRateHealthyLimit"
                            :progress-bar-units="'%'"
                            :legend-hover-label="'bounces'"
                            :link-label="'Recent bounces'"
                            link-href="#bounce-report"
                            :empty-state-message="'No email bounces have been recorded for the current reporting period'"
                        />
                    </ds-card-body>
                </ds-card>

                <description-card
                    class="card"
                    header-text="What is an email bounce?"
                    :link-address="learnMoreLinkAddress"
                    type="bounce"
                >
                    <template v-slot:content>
                        <div v-if="isEmailHealthContentUpdateEnabled">
                            <p>A bounce occurs when an email can’t be delivered and is rejected by the recipient’s server. That server then sends a response back to Keap explaining why the delivery failed.</p>

                            <p>Bounces can happen for several reasons—such as an invalid address, a full inbox, a blocked message, or spam filtering.</p>

                            <span>Keap's <strong>Invalid Bounce </strong> stat specifically tracks emails that fail due to <strong>invalid or non-existent addresses, </strong>which can negatively impact your sender reputation.There are two main types of bounces:</span>
                            <br />
                            <ul class="ul">
                                <li><strong>Hard Bounces - </strong>Permanent failures (e.g., invalid address, domain doesn't exist, or email blocked)</li>
                                <li><strong>Soft Bounces - </strong>Temporary issues (e.g., full inbox, server timeout)</li>
                            </ul>

                            <p>Additional bounce classifications that fall under these types include:</p>
                            <p><strong>General, Mail Block, Unsubscribe,</strong> and <strong>Undetermined,</strong> which offer more context on why the message was rejected.</p>
                        </div>


                        <div v-else>
                            <p>Most emails bounce because of a permanent issue with the receiving email account, a temporary issue with the receiving email account, or because the email is blocked by the receiving server.</p>

                            <p>When an email bounces, the recipient's server sends a message back to Keap that describes the reason for the bounce.</p>

                            <p>There are 6 types of bounces, General bounce, Hard bounce, Soft bounce, Main blocks, Unsubscribe requests and Undetermined. To learn more about bounces and how to avoid them follow the link below.</p>
                        </div>
                    </template>
                </description-card>

                <ds-card :class="getClass()">
                    <ds-card-header class="card-title">
                        <div class="email-card-header">
                            Take action to reduce email bounces
                        </div>

                        <div class="last-30-days">
                            Recommended actions
                        </div>
                    </ds-card-header>

                    <ds-card-body>
                        <bounce-take-action-checklist-card
                            :is-unengaged-email-status-processing-enabled="isUnengagedEmailStatusProcessingEnabled"
                            :is-update-help-article-links-enabled="isUpdateHelpArticleLinksEnabled"
                            :app-edition="appEdition"
                        />
                    </ds-card-body>
                </ds-card>

                <EmailRehabServiceCard
                    v-if="displayRehabServiceCard"
                    class="card"
                    icon-path="/resources/svg/customer-support.svg"
                    :header-text="$t('email.healthWidget.rehabServiceTile.bounces.headerText')"
                    :body-text="$t('email.healthWidget.rehabServiceTile.bounces.bodyText')"
                    :link-address="rehabServiceLinkAddress"
                    :help-button-label="$t('email.healthWidget.rehabServiceTile.bounces.helpButtonLabel')"
                />
            </div>
        </div>

        <div class="bounce-report-container">
            <bounce-report id="bounce-report" :timezone="timezone" />
        </div>
    </div>
</template>

<script>
import ProgressBarPiChartCard from './ProgressBarPiChartCard';
import DescriptionCard from './shared/DescriptionCard';
import BounceReport from './BounceReport';
import BounceTakeActionChecklistCard from './BounceTakeActionChecklistCard';
import axios from 'axios';
import { complianceThresholds } from '@/utils/email.util';
import ShareFeedbackModal from '@/components/shared/ShareFeedbackModal';
import { EMAIL_ANALYTICS } from '@/components/shared/constants/shareFeedback.constants';
import { SLACK_CHANNELS } from '@/components/util/slack/slack.constants';
import EmailRehabServiceCard from './shared/EmailRehabServiceCard';

export default {
    EMAIL_ANALYTICS,
    SLACK_CHANNELS,

    components: {
        EmailRehabServiceCard,
        DescriptionCard,
        ProgressBarPiChartCard,
        BounceReport,
        BounceTakeActionChecklistCard,
        ShareFeedbackModal,
    },

    props: {
        appId: String,
        appEdition: String,
        isEmailRehabServiceEnabled: {
            type: Boolean,
            default: false,
        },
        firstName: String,
        lastName: String,
        timezone: String,
        phoneNumber: String,
        email: String,
        isEmailHealthContentUpdateEnabled: {
            type: Boolean,
            default: false,
        },
        isUpdateHelpArticleLinksEnabled: {
            type: Boolean,
            default: false,
        },
        isUnengagedEmailStatusProcessingEnabled: Boolean,
        isEmailLockedDown: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: true,
            errorLoading: false,
            bounceRate: 0,
            emailBouncesByType: [],
            alertBannerMessage: '',
            bounceRateUpperLimit: null,
            bounceRateHealthyLimit: null,
            isFeedbackModalOpen: false,
        };
    },

    created() {
        this.load();
    },

    computed: {
        displayRehabServiceCard() {
            return this.isEmailRehabServiceEnabled && this.bounceRate > this.bounceRateUpperLimit;
        },

        rehabServiceLinkAddress() {
            return `https://keap.com/email-deliverability-consultation?appId=${this.appId}&firstName=${this.firstName}&lastName=${this.lastName}&email=${this.email}&phone=${this.phoneNumber}`;
        },

        learnMoreLinkAddress() {
            if (this.appEdition === 'Keap Max Classic Edition') {
                return 'https://help.infusionsoft.com/help/email-bounces-explained';
            }

            return 'https://help.keap.com/help/email-bounces-explained';
        },

        error_pulling_data () {
            return this.loading === false && this.errorLoading === true;
        },

        getAlertBannerMessage() {
            if (this.isEmailLockedDown) {
                return 'Due to a compliance issue your ability to send email broadcasts has been disabled. '
                        + 'Contact support for details on how to re-enable broadcast sending.';
            }

            if (this.error_pulling_data) {
                return 'An issue was encountered while retrieving your data. Please try reloading the page.';
            }

            if (this.bounceRate > complianceThresholds.bounceRateHealthyLimit) {
                return 'You are about to exceed the bounce limit. '
                        + 'If you do not take action soon, you may fall out of Keap’s Email Compliance Policy and if so, email sending restrictions may occur.';
            }

            return 'Your bounce rate is good.';
        },

        showAlertBannerLink() {
            return this.isEmailLockedDown;
        },

        showMessage() {
            return this.error_pulling_data
                    || this.isEmailLockedDown;
        },

    },

    methods: {
        getClass() {
            if (this.displayRehabServiceCard) {
                return 'card';
            }

            return 'single-line-card';
        },

        async load() {
            await this.loadMetrics();
        },

        async loadMetrics() {
            try {
                const metricsResponse = await axios.get('/app/metrics/getBouncePageMetrics');

                this.bounceRate = metricsResponse.data.emailHealthMetrics.bounceRate;
                this.emailBouncesByType = metricsResponse.data.emailBounceByTypeList
                    .map((bounce) => { return { type: bounce.bounceType, number: bounce.numberOfBounces }; });

                this.bounceRateHealthyLimit = complianceThresholds.bounceRateHealthyLimit;
                this.bounceRateUpperLimit = complianceThresholds.bounceRateUpperLimit;

                this.loading = false;
            } catch (e) {
                this.loading = false;
                this.errorLoading = true;
            }
        },
    },

};

</script>

<style lang="scss" rel="stylesheet/scss" scoped>
.alert-banner {
    display: flex;
}

.bounce-details-page {
    display: flex;
    justify-content: space-around;
    flex-wrap: wrap;
}

.bounce-report-container {
    margin: auto;
    width: 90vw;
}

.email-page-header {
    display: flex;
    align-items: center;
    padding-bottom: 0;
}

.title-arrow {
    font-size: $font-size-jumbo;
    align-items: center;
    flex: 50%;
    display: flex;
}

.header-with-tiles {
    max-width: 72rem;
    margin: auto;
}

.card {
    margin: $spacing-100;
    width: calc(50% - 1.5em);
}

.single-line-card {
    margin: $spacing-100;
    width: calc(100% - 1.5em);
}

.email-card-header {
    flex: 50%;
    float: left;
    font-size: $font-size-lg;
    padding-top: $spacing-100;
    padding-left: $spacing-200;
}

.last-30-days {
    font-size: $font-size-xs;
    padding-top: $spacing-100;
    padding-left: $spacing-200;
    text-align: left;
}

.feedback-link {
    display: flex;
    font-size: $font-size-sm;
    align-items: center;
    color: $color-blue-700;
}

.card-title {
    display: flex;
    flex-direction: column;
    border-bottom: solid px-to-rem(1) $color-ink-200;
    padding-bottom: $spacing-100;
}

</style>
